import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { BoltIcon, CheckCircleIcon, HandshakeIcon, ScaleIcon, SearchIcon, ShieldCheckIcon, TargetIcon } from './icons'
import { MarketingHeader } from './MarketingHeader'
import { MarketingFooter } from './MarketingFooter'
import { upsertJsonLd, removeJsonLd } from '../../seo/useSeo'
import { track } from '../../analytics/track'
import { getLandingPagesByTopic } from '../../content/landingPages'
import { resolveSupportedLang } from '../../i18n'

interface B2bLandingPageProps {
  onSelectCustomer: () => void
  onSelectCraftsman: () => void
  onLoginCustomer: () => void
  onLoginCraftsman: () => void
}

interface Step {
  title: string
  text: string
}

interface FaqItem {
  q: string
  a: string
}

/**
 * B2B-Subunternehmer-Marktplatz-Landingpage (docs/tasks/active/features/b2b/03-b2b-landingpage-content.md) -
 * strukturell eine Kopie des Sektionen-Musters aus LandingPage.tsx (Hero → USP → Ablauf →
 * Vergleich → FAQ → Final-CTA), aber ausschließlich an Handwerksbetriebe gerichtet: kein
 * PV-Rechner-/Blog-Teaser-Bezug zu Endkunden-Inhalten. CTAs führen zu onSelectCraftsman/
 * onLoginCraftsman - gleicher Account/Login wie im bestehenden Handwerker-Bereich (siehe
 * 02-frontend-handwerker-flow.md). Hostname-basiertes Rendering unter b2b.roofora.de ist Teil von
 * 04-seo-subdomain-deployment.md, hier geht es nur um Komponente + Inhalt.
 */
const FAQ_JSON_LD_ID = 'b2b-landing-faq-jsonld'

export function B2bLandingPage({ onSelectCustomer, onSelectCraftsman, onLoginCustomer, onLoginCraftsman }: B2bLandingPageProps) {
  const { t, i18n } = useTranslation()
  const trustItems = t('b2bLanding.hero.trust', { returnObjects: true }) as string[]
  const steps = t('b2bLanding.steps.items', { returnObjects: true }) as Step[]
  const comparisonBad = t('b2bLanding.comparison.bad.items', { returnObjects: true }) as string[]
  const comparisonGood = t('b2bLanding.comparison.good.items', { returnObjects: true }) as string[]
  const faqItems = t('b2bLanding.faq.items', { returnObjects: true }) as FaqItem[]
  // GitLab-Issue #145 (Schritt 5): dezenter How-to-Link analog zu howToCustomerHref/
  // howToCraftsmanHref in LandingPage.tsx - der B2B-Marktplatz hat nur eine Zielgruppe
  // (Handwerksbetriebe), daher genau ein Link statt zweier. Die Guide-Seite existiert aktuell nur
  // auf Deutsch (siehe content/landingPages/so-funktioniert-es-fuer-b2b-marktplatz.ts) - Fallback
  // auf die DE-Variante, solange ES/PL noch fehlen, statt eines kaputten "/undefined"-Links.
  const lang = resolveSupportedLang(i18n.resolvedLanguage)
  const b2bHowToPages = getLandingPagesByTopic('so-funktioniert-es-fuer-b2b-marktplatz')
  const howToB2bHref = `/${(b2bHowToPages.find((page) => page.lang === lang) ?? b2bHowToPages.find((page) => page.lang === 'de'))?.slug}`

  // Same gap/reasoning as LandingPage.tsx's homepage FAQPage schema - visible FAQ section below
  // had no matching structured data.
  useEffect(() => {
    upsertJsonLd(FAQ_JSON_LD_ID, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: (t('b2bLanding.faq.items', { returnObjects: true }) as FaqItem[]).map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    })
    return () => removeJsonLd(FAQ_JSON_LD_ID)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.resolvedLanguage])

  // Gleiches "how_to_clicked"-Event wie trackHowToCraftsman in LandingPage.tsx, eigener
  // location-Wert ("b2b_hero") für die Funnel-Auswertung.
  function trackHowTo() {
    track('how_to_clicked', { role: 'craftsman', location: 'b2b_hero' })
  }

  return (
    <div className="landing">
      {/* logoHref zeigt bewusst auf die Hauptseite (roofora.de), nicht "#top"/"/" wie bei
       *  LandingPage - b2b.roofora.de ist eine eigene Subdomain, das Logo muss also cross-domain
       *  verlinken statt nur zurück zu scrollen (siehe Task-Doku, Cross-Linking-Anforderung). */}
      <MarketingHeader
        logoHref="https://roofora.de/"
        onSelectCustomer={onSelectCustomer}
        onLoginCustomer={onLoginCustomer}
        onLoginCraftsman={onLoginCraftsman}
      />

      <main>
        <section className="hero" id="top">
          <div className="hero-content">
            <p className="eyebrow">{t('b2bLanding.hero.eyebrow')}</p>
            <h1>{t('b2bLanding.hero.title')}</h1>
            <p className="hero-sub">{t('b2bLanding.hero.subtitle')}</p>
            <div className="hero-actions">
              <button className="btn-primary" onClick={onSelectCraftsman}>
                {t('b2bLanding.hero.ctaPrimary')}
              </button>
              <button className="btn-secondary" onClick={onLoginCraftsman}>
                {t('b2bLanding.hero.ctaSecondary')}
              </button>
            </div>
            {/* GitLab-Issue #145 (Schritt 5): dezenter How-to-Link, gleiches Muster wie
             *  hero-how-to-links in LandingPage.tsx (#143/#144). */}
            <div className="hero-how-to-links">
              <Link to={howToB2bHref} className="hero-how-to-link" onClick={trackHowTo}>
                {t('b2bLanding.hero.howTo')}
              </Link>
            </div>
            <ul className="trust-row">
              {trustItems.map((item) => (
                <li key={item}>
                  <CheckCircleIcon /> {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="hero-visual">
            <div className="usp-card usp-card--highlight">
              <HandshakeIcon className="usp-icon" />
              <p className="hero-visual-slogan">{t('b2bLanding.hero.eyebrow')}</p>
            </div>
          </div>
        </section>

        <section className="usp" id="vorteile">
          <div className="usp-card">
            <TargetIcon className="usp-icon" />
            <h2>{t('b2bLanding.usp.capacitySpike.title')}</h2>
            <p>{t('b2bLanding.usp.capacitySpike.text')}</p>
          </div>
          <div className="usp-card">
            <SearchIcon className="usp-icon" />
            <h2>{t('b2bLanding.usp.freeCapacity.title')}</h2>
            <p>{t('b2bLanding.usp.freeCapacity.text')}</p>
          </div>
          <div className="usp-card">
            <ScaleIcon className="usp-icon" />
            <h2>{t('b2bLanding.usp.contactControl.title')}</h2>
            <p>{t('b2bLanding.usp.contactControl.text')}</p>
          </div>
          <div className="usp-card">
            <ShieldCheckIcon className="usp-icon" />
            <h2>{t('b2bLanding.usp.oneAccount.title')}</h2>
            <p>{t('b2bLanding.usp.oneAccount.text')}</p>
          </div>
        </section>

        <section className="steps" id="ablauf">
          <h2 className="section-title">{t('b2bLanding.steps.title')}</h2>
          <ol className="steps-list">
            {steps.map((step, i) => (
              <li key={step.title}>
                <span className="step-number">{i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="comparison">
          <h2 className="section-title">{t('b2bLanding.comparison.title')}</h2>
          <div className="comparison-grid">
            <div className="comparison-col comparison-col--bad">
              <h3>{t('b2bLanding.comparison.bad.title')}</h3>
              <ul>
                {comparisonBad.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="comparison-col comparison-col--good">
              <h3>{t('b2bLanding.comparison.good.title')}</h3>
              <ul>
                {comparisonGood.map((item) => (
                  <li key={item}>
                    <CheckCircleIcon /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="faq" id="faq">
          <h2 className="section-title">{t('b2bLanding.faq.title')}</h2>
          <div className="faq-list">
            {faqItems.map((item) => (
              <details key={item.q} className="faq-item">
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Cross-Linking zur Hauptseite (siehe Task-Doku) - MarketingFooter wird bewusst
         *  unverändert wiederverwendet (Props-basiert, keine Anpassung nötig), daher hier ein
         *  eigenes Band statt eines Footer-Umbaus. */}
        <section className="craftsman-band">
          <TargetIcon className="craftsman-band-icon" />
          <div>
            <h2>{t('b2bLanding.crossLinkBand.title')}</h2>
            <p>{t('b2bLanding.crossLinkBand.text')}</p>
          </div>
          <a href="https://roofora.de/" className="btn-secondary">
            {t('b2bLanding.backToMainSite')}
          </a>
        </section>

        <section className="final-cta">
          <BoltIcon className="final-cta-icon" />
          <h2>{t('b2bLanding.finalCta.title')}</h2>
          <p>{t('b2bLanding.finalCta.text')}</p>
          <button className="btn-primary" onClick={onSelectCraftsman}>
            <BoltIcon /> {t('b2bLanding.finalCta.cta')}
          </button>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}
